import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ITenant, Tenant } from '@shared/models/tenant';
import { U2bAdminTenantsApi } from '@modules/admin/state/tenants/u2b-admin-tenants.api';
import { U2bAdminBaseFacade } from '@modules/admin/state/u2b-admin-base.facade';
import { AppUser } from '@core/services/user.service';
import { ConfirmDialogService } from '@sharedComponents/dialogs/confirm-dialog/confirm-dialog.service';
import { TranslationService } from '@core/translation/translation.service';
import {
    AdminTenantModulePermission,
    AdminTenantModulePermissionUpdate,
} from '@modules/admin/tenants/models/admin-tenant-module-permission.model';
import {
    AdminTenantOrderManagementRollout,
    AdminTenantOrderManagementRolloutUpdate,
} from '@modules/admin/tenants/models/admin-tenant-order-management-rollout.model';
import {
    TenantTseIntegrationDto,
    TenantTseIntegrationTestResultDto,
    TenantTseIntegrationUpsertDto,
} from '@shared/models/tenant-tse-integration.model';
import {
    AdminBcmPermissionDescriptionDto,
    U2bAdminBcmPermissionsApi,
} from '@modules/admin/state/bcm-permissions/u2b-admin-bcm-permissions.api';
import { AdminMysqlInfrastructureDto } from '@modules/admin/tenants/models/admin-tenant-infrastructure.model';
import {
    AdminTenantSupportWhitelistEntry,
    AdminTenantSupportWhitelistUserSearchResult,
} from '@modules/admin/tenants/models/admin-tenant-support-whitelist.model';

const tenantResourceConfig = {
    viewNameSingular: 'Kunde',
    viewNamePlural: 'Kunden',
    endpointPath: 'tenants',
    entityClass: Tenant
};

@Injectable({providedIn: 'root'})
export class U2bAdminTenantsFacade extends U2bAdminBaseFacade<Tenant, ITenant> {

    protected readonly api = new U2bAdminTenantsApi(
        this.httpClient,
        this.confirmDialogService,
        tenantResourceConfig
    );

    private readonly bcmPermissionsApi = new U2bAdminBcmPermissionsApi(this.httpClient);

    private readonly _infrastructure$ = new BehaviorSubject<AdminMysqlInfrastructureDto | null>(null);

    get infrastructure$(): Observable<AdminMysqlInfrastructureDto | null> {
        return this._infrastructure$.asObservable();
    }

    get tenants$(): Observable<Tenant[]> {
        return this.state.list$;
    }

    get selected$(): Observable<Tenant> {
        return this.state.selected$;
    }

    get isLoadingAll$(): Observable<boolean> {
        return this.state.isUpdatingAll$;
    }

    get isLoading$(): Observable<boolean> {
        return this.state.isUpdating$;
    }

    constructor(httpClient: HttpClient,
                confirmDialogService: ConfirmDialogService) {
        super(httpClient, confirmDialogService, tenantResourceConfig);
    }

    loadAll(): Observable<Tenant[]> {
        return this.api.loadAllWithInfrastructure()
            .pipe(
                tap(({ tenants, infrastructure }) => {
                    this._infrastructure$.next(infrastructure);
                    this.state.setList(tenants);
                }),
                map(({ tenants }) => tenants),
            );
    }

    setMysqlEventScheduler(enabled: boolean): Observable<AdminMysqlInfrastructureDto> {
        return this.api.updateMysqlEventScheduler(enabled)
            .pipe(
                tap((infrastructure) => this._infrastructure$.next(infrastructure)),
            );
    }

    setSubscriptionScheduler(tenant: Tenant, enabled: boolean): Observable<Tenant> {
        return this.api.updateSubscriptionScheduler(tenant, enabled)
            .pipe(map((result) => {
                const updatedTenant = new Tenant({
                    ...(tenant as unknown as ITenant),
                    subscriptionSchedulerEnabled: result.subscriptionSchedulerEnabled,
                    subscriptionSchedulerAvailable: result.subscriptionSchedulerAvailable,
                });
                this.state.replaceListItem(tenant, updatedTenant);

                return updatedTenant;
            }));
    }

    setIsDemo(tenant: Tenant, isDemo: boolean, isDemoUntil?: Date): Subscription {
        if (!tenant?.id) {
            return;
        }

        return this.api.update(tenant, {isDemo, isDemoUntil})
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    return throwError(error);
                })
            )
            .subscribe((updatedTenant) => this.state.replaceListItem(tenant, updatedTenant));
    }

    setDisabled(tenant: Tenant, disabled: boolean, disableReason?: string): Subscription {
        if (!tenant?.id) {
            return;
        }

        return this.api.update(tenant, {disabled: !disabled, disableReason})
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    return throwError(error);
                })
            )
            .subscribe((updatedTenant) => this.state.replaceListItem(tenant, updatedTenant));
    }

    setPleskBackup(tenant: Tenant, enabled: boolean): Observable<Tenant> {
        return this.api.updatePleskBackup(tenant, enabled)
            .pipe(map((result) => {
                const updatedTenant = new Tenant({
                    ...(tenant as unknown as ITenant),
                    tenantDbName: result.tenantDbName,
                    pleskBackupRegistered: result.pleskBackupRegistered,
                    pleskBackupRegistryAvailable: result.pleskBackupRegistryAvailable,
                });
                this.state.replaceListItem(tenant, updatedTenant);

                return updatedTenant;
            }));
    }

    // removeTenant(tenant: Tenant): Observable<any> {
    //
    //     if (tenant?.id) {
    //
    //         this.state.removeListItem(tenant);
    //
    //         return this.api.remove(tenant)
    //             .pipe(
    //                 catchError((error: HttpErrorResponse) => {
    //                     this.state.addListItem(tenant);
    //                     return throwError(error);
    //                 })
    //             );
    //     }
    //
    //     return throwError(TranslationService.translate('common.checkYourEntries'));
    // }


    /**
     * Benutzer Verwaltung
     */
    addUser(tenant: Tenant, user: Partial<AppUser>): Observable<AppUser> {
        return this.api.addUser(tenant, user);
    }

    removeUser(tenant: Tenant, user: AppUser): Observable<unknown> {
        return this.api.removeUser(tenant, user);
    }

    getModulePermissions(tenant: Tenant): Observable<AdminTenantModulePermission[]> {
        return this.api.getModulePermissions(tenant);
    }

    getOrderManagementRollouts(tenant: Tenant): Observable<AdminTenantOrderManagementRollout[]> {
        return this.api.getOrderManagementRollouts(tenant);
    }

    updateWithModulePermissions(
        tenant: Tenant,
        updateData: Partial<Tenant>,
        modulePermissions: AdminTenantModulePermissionUpdate[],
    ): Observable<Tenant> {
        this.state.startUpdating();

        return this.api.updateWithModulePermissions(tenant, {
            ...updateData,
            bcmModulePermissions: modulePermissions,
        }).pipe(tap((updatedTenant) => {
            this.state.replaceListItem(tenant, updatedTenant);
            this.state.stopUpdating();
        }));
    }

    updateOrderManagementRollouts(
        tenant: Tenant,
        rollouts: AdminTenantOrderManagementRolloutUpdate[],
    ): Observable<AdminTenantOrderManagementRollout[]> {
        return this.api.updateOrderManagementRollouts(tenant, rollouts);
    }

    getTenantTseIntegration(tenant: Tenant): Observable<TenantTseIntegrationDto> {
        return this.api.getTenantTseIntegration(tenant);
    }

    saveTenantTseIntegration(
        tenant: Tenant,
        payload: TenantTseIntegrationUpsertDto,
    ): Observable<TenantTseIntegrationDto> {
        return this.api.saveTenantTseIntegration(tenant, payload);
    }

    testTenantTseIntegration(
        tenant: Tenant,
        payload: TenantTseIntegrationUpsertDto,
    ): Observable<TenantTseIntegrationTestResultDto> {
        return this.api.testTenantTseIntegration(tenant, payload);
    }

    getSupportWhitelist(tenant: Tenant): Observable<AdminTenantSupportWhitelistEntry[]> {
        return this.api.getSupportWhitelist(tenant);
    }

    searchSupportWhitelistUsers(
        tenant: Tenant,
        query: string,
    ): Observable<AdminTenantSupportWhitelistUserSearchResult[]> {
        return this.api.searchSupportWhitelistUsers(tenant, query);
    }

    addSupportWhitelistUser(tenant: Tenant, userId: string): Observable<AdminTenantSupportWhitelistEntry> {
        return this.api.addSupportWhitelistUser(tenant, userId);
    }

    patchSupportWhitelistEntry(
        tenant: Tenant,
        entryId: number,
        enabled: boolean,
    ): Observable<AdminTenantSupportWhitelistEntry> {
        return this.api.patchSupportWhitelistEntry(tenant, entryId, enabled);
    }

    deleteSupportWhitelistEntry(tenant: Tenant, entryId: number): Observable<{ ok: boolean }> {
        return this.api.deleteSupportWhitelistEntry(tenant, entryId);
    }

    updatePermissionDescription(
        permissionId: number,
        permissionDescription: string | null,
    ): Observable<AdminBcmPermissionDescriptionDto> {
        return this.bcmPermissionsApi.updatePermissionDescription(permissionId, permissionDescription);
    }
}
